import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Collapse
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import EditCategoryDialog from './EditCategoryDialog';

const CategoryList = ({ categories, language, onCategoriesUpdate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const handleToggleExpand = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const getNameByLanguage = (category) => {
    switch (language) {
      case 'en':
        return category.name_en;
      case 'fin':
        return category.name_fin;
      case 'de':
        return category.name_de;
      case 'fr':
        return category.name_fr;
      default:
        return category.name_en;
    }
  };

  const mainCategories = categories.filter(cat => cat.parent_id === '0');
  
  const getSubcategories = (parentId) => {
    return categories.filter(cat => cat.parent_id === parentId);
  };

  const filteredCategories = mainCategories.filter(category => {
    const name = getNameByLanguage(category).toLowerCase();
    const hasMatchingSubcategory = getSubcategories(category.id).some(sub => 
      getNameByLanguage(sub).toLowerCase().includes(searchTerm.toLowerCase())
    );
    return name.includes(searchTerm.toLowerCase()) || hasMatchingSubcategory;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleEditClick = (category) => {
    setSelectedCategory(category);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedCategory(null);
  };

  const handleEditSave = () => {
    onCategoriesUpdate();
    handleEditClose();
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search categories..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="50"></TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell align="center">Subcategories</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCategories.map((category) => {
              const subcategories = getSubcategories(category.id);
              const hasSubcategories = subcategories.length > 0;
              const isExpanded = expandedCategories[category.id];
              
              return (
                <React.Fragment key={category.id}>
                  <TableRow hover>
                    <TableCell>
                      {hasSubcategories && (
                        <IconButton
                          size="small"
                          onClick={() => handleToggleExpand(category.id)}
                        >
                          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>{category.id}</TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold">
                        {getNameByLanguage(category)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {hasSubcategories && (
                        <Chip 
                          label={subcategories.length} 
                          size="small" 
                          color="primary"
                        />
                      )}
                    </TableCell>
                    <TableCell>{formatDate(category.create_date)}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(category)}
                        title="Edit translations"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  
                  {hasSubcategories && (
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 2 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>ID</TableCell>
                                  <TableCell>Subcategory Name</TableCell>
                                  <TableCell>Created Date</TableCell>
                                  <TableCell align="center">Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {subcategories
                                  .filter(sub => {
                                    const name = getNameByLanguage(sub).toLowerCase();
                                    return name.includes(searchTerm.toLowerCase());
                                  })
                                  .map((subcategory) => (
                                  <TableRow key={subcategory.id}>
                                    <TableCell>{subcategory.id}</TableCell>
                                    <TableCell sx={{ pl: 4 }}>
                                      {getNameByLanguage(subcategory)}
                                    </TableCell>
                                    <TableCell>{formatDate(subcategory.create_date)}</TableCell>
                                    <TableCell align="center">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleEditClick(subcategory)}
                                        title="Edit translations"
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Total: {mainCategories.length} main categories, {categories.length - mainCategories.length} subcategories
        </Typography>
      </Box>

      <EditCategoryDialog
        open={editDialogOpen}
        onClose={handleEditClose}
        category={selectedCategory}
        onSave={handleEditSave}
      />
    </Box>
  );
};

export default CategoryList;